import React, { useState, useContext, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Text,
  TableContainer, Table, Thead, Tbody, Tr, Th, Td,
  Textarea,
  Select,
  Checkbox,
  Link,
  useToast,
  Portal,
  Flex
} from "@chakra-ui/react";

import Draggable from "react-draggable";

import { WebAPIContext } from '../../contexts/WebAPIContext';
import { ProjectContext } from "./InvestmentScreeningDetail";
import { CheckListDataType, PagePermissions, CheckListDataMap, ChecklistRecord } from '../../contexts/types';
import {
  ApplicableCodeToNameMap,
  Messages,
} from '../../contexts/constants';
import '../common.css';
import HelpIcon from "./HelpIcon";

/** 遊休設備確認チェックのスナップショット保存キー */
const IDLE_EQUIPMENT_CHECK_KEY = 'idleEquipmentChecked';
/** 遊休設備確認ポータルURL */
const IDLE_EQUIPMENT_PORTAL_URL = 'https://portal-minebea.app.lumapps.com/site-list/ls/content/1467325079239043/1467325079239043';
/** 遊休設備確認に対する問い合わせ先URL */
const IDLE_EQUIPMENT_INQUIRY_URL = 'https://portal-minebea.app.lumapps.com/site-list/ls/content/4156288556360214/4156288556360214';

/** １０か条チェックリスト確認チェックのスナップショット保存キー */
const TEN_PRINCIPLES_CHECK_KEY = 'tenPrinciplesChecked';
/** １０か条チェックリスト確認ポータルURL（本番URL未決定のため仮設定） */
const TEN_PRINCIPLES_PORTAL_URL = 'https://portal-minebea.app.lumapps.com/home/ls/content/804067131309523/804067131309523';

/**
 * チェックリストパラメータ
 */
interface CheckListProps {
  /** プロジェクトID */
  projectId?: string | null;
  /** チェックリスト */
  checkList?: string | null;
  /** 更新日時 */
  updateDate?: string | null;
  /** チェックリスト情報更新関数 */
  setCheckListDataList?: React.Dispatch<React.SetStateAction<CheckListDataMap>>;
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** 設備ID */
  equipmentId?: string | null;
  /** チェックリストマスタデータ（親コンポーネントから渡される） */
  masterList?: ChecklistRecord[];
}

/**
 * １０か条チェックリスト
 */
const CheckList: React.FC<CheckListProps> = ({ projectId, equipmentId, checkList, updateDate, setCheckListDataList, pagePermissions, masterList = [] }) => {

  const { t, i18n } = useTranslation();

  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.CheckList.title');

  /** 行高さ（旧・表形式チェックリスト用。外部リンク＋確認方式への変更に伴い現状未使用。表を復活させる場合に再利用） */
  const ROW_HEIGHT = 60;
  /** ポップアップ本体幅（px）
   * 表形式チェックリストを廃止し、リンク＋確認チェックのみの構成になったため、
   * 画面幅に対する比率指定ではなく内容量に応じた固定サイズに変更
   */
  const POPUP_WIDTH_PX = 1000;
  const POPUP_WIDTH = `${POPUP_WIDTH_PX}px`;
  /** ポップアップ本体高さ（内容量に応じて自動算出。表形式チェックリスト廃止に伴い固定vh比率指定をやめる） */
  const POPUP_HEIGHT = "auto";
  /** ポップアップ本体の最大高さ（画面からあふれる場合のみスクロールさせるための保険） */
  const POPUP_MAX_HEIGHT = "90vh";
  /** 表示位置算出用の高さ推定値（px）。実際の高さは内容量に応じて可変（POPUP_HEIGHT="auto"）のため、初期中央表示の計算にのみ使用 */
  const POPUP_EST_HEIGHT_PX = 460;

  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** 振り返りモード */
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;

  /** 表示制御用 */
  const [isOpen, setIsOpen] = useState(false);
  /** 表示位置制御用 */
  const [position, setPosition] = useState({ x: 0, y: 0 });

  /** チェックリストデータ */
  const [checkListData, setCheckListData] = useState<CheckListDataType>({});

  /** 遊休設備確認チェック（リンク先確認） */
  const [idleEquipmentChecked, setIdleEquipmentChecked] = useState<boolean>(false);

  /** １０か条チェックリスト確認チェック（リンク先確認） */
  const [tenPrinciplesChecked, setTenPrinciplesChecked] = useState<boolean>(false);

  /** 編集制御用フラグ */
  const isEditable = pagePermissions?.isEditable ?? true;

  /** スナップショット使用フラグ: 申請後 または 振り返りモード時はスナップショットを使う */
  const useSnapshot = !isEditable || !!reviewDate;

  /** マスタ・スナップショット共用: 現在の言語に応じてコンテンツ名を返す */
  const getContentName = (item: { contentNameJa?: string; contentNameEn?: string }): string =>
    (i18n.language === 'en' && item.contentNameEn) ? item.contentNameEn : (item.contentNameJa ?? "");

  /** マスタ・スナップショット共用: 現在の言語に応じて懸念点リストを返す */
  const getConflictingConsiderations = (item: ChecklistRecord): string[] => {
    const jaList = item.conflictingConsiderationsJa ?? [];
    return (i18n.language === 'en' && item.conflictingConsiderationsEn?.length) ? item.conflictingConsiderationsEn : jaList;
  };

  /**
   * 「１０か条チェックリスト」ボタン押下
   */
  const handleOpenCheckList = () => {
    // ↓↓↓ 表形式チェックリスト運用時のマスタ未登録チェック。外部リンク＋確認方式への変更に伴い無効化（表を復活させる場合に再利用）
    // // スナップショット（checkList あり）以外でmasterListがない場合はマスタ未登録トーストを出して終了
    // if (masterList.length === 0 && (!useSnapshot || !checkList)) {
    //   toast({
    //     title: TITLE,
    //     description: t('features.InvestmentScreening.CheckList.message.masterNotRegistered'),
    //     status: 'warning',
    //     duration: 5000,
    //     isClosable: true,
    //   });
    //   return;
    // }
    // ↑↑↑

    // 画面中央の座標を計算（高さは内容量により可変のため、初期表示位置の算出には推定値を使用）
    const centerX = (window.innerWidth / 2) - (POPUP_WIDTH_PX / 2);
    const centerY = Math.max(0, (window.innerHeight / 2) - (POPUP_EST_HEIGHT_PX / 2));
    setPosition({ x: centerX, y: centerY });

    if (checkList) {
      try {
        const rawData = JSON.parse(checkList);

        // 遊休設備確認チェック値設定
        setIdleEquipmentChecked(!!rawData[IDLE_EQUIPMENT_CHECK_KEY]);
        // １０か条チェックリスト確認チェック値設定
        setTenPrinciplesChecked(!!rawData[TEN_PRINCIPLES_CHECK_KEY]);

        // ↓↓↓ 表形式チェックリストのデータ読込・変換処理。外部リンク＋確認方式への変更に伴い無効化（表を復活させる場合に再利用）
        // if (useSnapshot) {
        //   // スナップショットモード: masterList に依存せず DB の保存値をそのままロード
        //   const snapshotLoaded: CheckListDataType = {};
        //   Object.entries(rawData).forEach(([no, group]: [string, any]) => {
        //     const extractedCheckItems: any = {};
        //     Object.keys(group.checkItems ?? {}).forEach((nodeId) => {
        //       const item = group.checkItems[nodeId];
        //       extractedCheckItems[nodeId] = typeof item === 'object' ? (item.value ?? "") : item;
        //     });
        //     snapshotLoaded[no] = { memo: group.memo ?? "", checkItems: extractedCheckItems };
        //   });
        //   setCheckListData(snapshotLoaded);
        // } else {
        //   // マスタモード: 旧データ形式（a, b, cキー）が混在している場合は最新のUUIDキー形式に統一
        //   const convertedData = convertOldDataToNewFormat(rawData, masterList);
        //   setCheckListData(convertedData);
        // }
        // ↑↑↑

      } catch (e) {
        console.error(`failed checkList String to Object`, e);
      }
    } else {
      // setCheckListData({}); // 表形式チェックリスト用（現在未使用）
      setIdleEquipmentChecked(false);
      setTenPrinciplesChecked(false);
    }

    setIsOpen(true);
  }

  // ↓↓↓ 表形式チェックリスト用データ変換処理。外部リンク＋確認方式への変更に伴い無効化（表を復活させる場合に再利用）
  // /**
  //  * 過去のデータ構造（a, b, cキー）を最新のUUIDキー構造へ変換
  //  */
  // const convertOldDataToNewFormat = (rawData: any, currentMaster: ChecklistRecord[]): CheckListDataType => {
  //   const result: any = {};
  //   const principles = currentMaster.filter(row => row.nodeClass === 'PRINCIPLE');
  //
  //   principles.forEach((p) => {
  //     const targetNo = p.checklistNo;
  //     if (!rawData[targetNo]) return;
  //
  //     // 既に新データ形式（nodeIdを保持）の場合は、オブジェクト内のスナップショットから値を抽出してフラットにロード
  //     if (rawData[targetNo].nodeId) {
  //       const savedCheckItems = rawData[targetNo].checkItems ?? {};
  //       const extractedCheckItems: any = {};
  //
  //       // 保存されている各UUIDキーのオブジェクトから、valueの値だけを抽出してState用のフラットな型に復元
  //       Object.keys(savedCheckItems).forEach((nodeId) => {
  //         const itemSnapshot = savedCheckItems[nodeId];
  //         extractedCheckItems[nodeId] = itemSnapshot && typeof itemSnapshot === 'object' ? (itemSnapshot.value ?? "") : itemSnapshot;
  //       });
  //
  //       result[targetNo] = {
  //         memo: rawData[targetNo].memo ?? "",
  //         checkItems: extractedCheckItems
  //       };
  //       return;
  //     }
  //
  //     const oldCheckItems = rawData[targetNo].checkItems ?? {};
  //     const newCheckItems: any = {};
  //     const mItems = currentMaster.filter(row => row.nodeClass === 'ITEM' && row.parentNodeId === p.nodeId);
  //
  //     mItems.forEach((item) => {
  //       // orderからアルファベットキーを算出して詰め替え
  //       const subKey = String.fromCharCode(96 + item.order);
  //       if (oldCheckItems[subKey] !== undefined) {
  //         newCheckItems[item.nodeId] = oldCheckItems[subKey];
  //       }
  //     });
  //
  //     // その他行のマイグレーション
  //     const oldOtherKey = String.fromCharCode(96 + (mItems.length + 1));
  //     if (oldCheckItems[oldOtherKey] !== undefined) {
  //       newCheckItems[`${p.nodeId}_other`] = oldCheckItems[oldOtherKey];
  //     }
  //
  //     result[targetNo] = {
  //       memo: rawData[targetNo].memo ?? "",
  //       checkItems: newCheckItems
  //     };
  //   });
  //
  //   return result;
  // };
  // ↑↑↑

  // ↓↓↓ 表形式チェックリスト用の入力変更処理。外部リンク＋確認方式への変更に伴い無効化（表を復活させる場合に再利用）
  // /**
  //  * チェックリスト該当の変更処理
  //  * @param key 大項目のchecklistNo
  //  * @param itemNodeId 子項目のnodeId (UUID)
  //  * @param newValue 設定値
  //  */
  // const changeCheckItems = (key: string, itemNodeId: string, newValue: any) => {
  //   setCheckListData((prevState) => ({
  //     ...prevState,
  //     [key]: {
  //       ...prevState[key],
  //       checkItems: {
  //         ...prevState[key]?.checkItems,
  //         [itemNodeId]: newValue,
  //       },
  //     },
  //   }));
  // }
  //
  // /**
  //  * メモ欄を変更する。
  //  * @param key キー
  //  * @param newValue 新しい値
  //  */
  // const changeMemo = (key: string, newValue: any) => {
  //   setCheckListData((prevState) => ({
  //     ...prevState,
  //     [key]: {
  //       ...prevState[key],
  //       memo: newValue,
  //     },
  //   }));
  // }
  // ↑↑↑

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    const query = `
      mutation updateCheckList($input: CheckListInput!) {
        updateCheckList(input: $input) {status}
      }
    `;

    // 外部リンク＋確認方式へ移行済みのため、保存時は旧形式（表形式チェックリストの過去データ等）を破棄し、
    // 確認チェックの2フラグのみを持つ新形式で保存する。
    // これにより、旧形式で保存されていた設備でもこの保存操作を行った時点で申請書（ReportAndDraft）は新形式表示に切り替わる。
    const snapshotPayload: any = {};

    // ↓↓↓ 表形式チェックリストのスナップショット再構築処理。外部リンク＋確認方式への変更に伴い無効化（表を復活させる場合に再利用）
    // if (useSnapshot && !!checkList) {
    //   // スナップショット: 保存済みの構造・文言を維持し、ユーザー入力（value/memo）のみ上書き
    //   try {
    //     const storedSnapshot = JSON.parse(checkList);
    //     Object.entries(storedSnapshot).forEach(([no, group]: [string, any]) => {
    //       if (no === IDLE_EQUIPMENT_CHECK_KEY) return;
    //       const stateGroup = checkListData[no];
    //       const updatedCheckItems: any = {};
    //
    //       Object.entries(group.checkItems ?? {}).forEach(([nodeId, itemSnapshot]: [string, any]) => {
    //         updatedCheckItems[nodeId] = {
    //           ...itemSnapshot,
    //           value: stateGroup?.checkItems?.[nodeId] ?? itemSnapshot.value ?? "",
    //         };
    //       });
    //
    //       snapshotPayload[no] = {
    //         ...group,
    //         memo: stateGroup?.memo ?? group.memo ?? "",
    //         checkItems: updatedCheckItems,
    //       };
    //     });
    //   } catch (e) {
    //     console.error('failed to parse checkList for snapshot save', e);
    //     return;
    //   }
    // } else {
    //   // masterList からスナップショットを再構築
    //   principles.forEach((p) => {
    //     const targetNo = p.checklistNo;
    //     const stateGroup = checkListData[targetNo];
    //
    //     const snapshotCheckItems: any = {};
    //     const mItems = masterList.filter(row => row.nodeClass === 'ITEM' && row.parentNodeId === p.nodeId);
    //
    //     // 子項目の構成（未選択項目も空文字列で構造を保証）
    //     mItems.forEach((item) => {
    //       const userValue = stateGroup?.checkItems?.[item.nodeId] ?? "";
    //       snapshotCheckItems[item.nodeId] = {
    //         contentNameJa: item.contentNameJa,
    //         ...(item.contentNameEn !== undefined && { contentNameEn: item.contentNameEn }),
    //         order: item.order,
    //         value: userValue
    //       };
    //     });
    //
    //     // その他行の構成（両言語のテキストを保存しタイムカプセル化）
    //     const otherNodeId = `${p.nodeId}_other`;
    //     const otherValue = stateGroup?.checkItems?.[otherNodeId] ?? "";
    //     snapshotCheckItems[otherNodeId] = {
    //       contentNameJa: i18n.t('features.InvestmentScreening.CheckList.other', { lng: 'ja' }),
    //       contentNameEn: i18n.t('features.InvestmentScreening.CheckList.other', { lng: 'en' }),
    //       order: mItems.length + 1,
    //       value: otherValue
    //     };
    //
    //     // 大項目情報と組み合わせてパッキング
    //     snapshotPayload[targetNo] = {
    //       nodeId: p.nodeId,
    //       contentNameJa: p.contentNameJa,
    //       ...(p.contentNameEn !== undefined && { contentNameEn: p.contentNameEn }),
    //       conflictingConsiderationsJa: p.conflictingConsiderationsJa,
    //       ...(p.conflictingConsiderationsEn !== undefined && { conflictingConsiderationsEn: p.conflictingConsiderationsEn }),
    //       memo: stateGroup?.memo ?? "",
    //       checkItems: snapshotCheckItems
    //     };
    //   });
    // }
    // ↑↑↑

    // 遊休設備確認チェックをスナップショットへ格納
    snapshotPayload[IDLE_EQUIPMENT_CHECK_KEY] = idleEquipmentChecked;
    // １０か条チェックリスト確認チェックをスナップショットへ格納
    snapshotPayload[TEN_PRINCIPLES_CHECK_KEY] = tenPrinciplesChecked;

    const result = await WebAPI.request({
      query: query,
      variables: {
        input: {
          projectId: projectId,
          equipmentId: equipmentId,
          checkList: JSON.stringify(snapshotPayload),
          updateDate: updateDate,
          ...(reviewDate && { reviewDate }),
        }
      }
    });

    console.log(`response = `, result);

    if (result?.data?.updateCheckList?.status === "success") {
      toast({
        title: t('features.InvestmentScreening.CheckList.message.saveSuccessTitle'),
        description: t('features.InvestmentScreening.CheckList.message.saveSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      await refreshProjectInfo();

      setIsOpen(false);
    } else if (result?.data?.updateCheckList.status === 'expired') {
      toast({
        title: TITLE,
        description: Messages.expiredMessage,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      console.warn(`Conflict error. updateCheckList.`);
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.CheckList.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API updateCheckList. errors = `, result?.errors);
    }
  }

  /**
   * １０か条チェックリスト再取得処理
   */
  const refreshProjectInfo = async () => {
    const payload = {
      query: `
        query getCheckListById($projectId: String, $reviewDate: String) {
            getCheckListById(projectId: $projectId, reviewDate: $reviewDate) {
              items {
                equipmentId
                checkList
                updateDate
              }
            }
        }
      `,
      variables: { projectId, ...(reviewDate && { reviewDate }) }
    };

    // APIリクエスト実行
    const response = await WebAPI.request(payload);

    if (response?.data?.getCheckListById) {
      const checkListByIdList = response?.data?.getCheckListById.items ?? [];
      for (const checkListById of checkListByIdList) {
        let equipmentId = checkListById.equipmentId;
        let newCheckList = checkListById.checkList;
        // 更新日時
        updateDate = checkListById.updateDate;

        // チェックリストを呼び出し元に反映
        setCheckListDataList?.((prevState: any) => ({
          ...prevState,
          [equipmentId]: {
            checkList: newCheckList,
            updateDate: checkListById.updateDate,
          },
        }));
      }

    } else {
      toast({
        title: t('features.InvestmentScreening.CheckList.message.fetchFailedTitle'),
        description: t('features.InvestmentScreening.CheckList.message.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API getProjectById. errors = `, response?.errors);
    }
  }

  // ↓↓↓ 表形式チェックリスト用の集計処理。外部リンク＋確認方式への変更に伴い無効化（表を復活させる場合に再利用）
  // // 大項目（PRINCIPLE）レコードのみを抽出（マスタモード・handleSave で使用）
  // const principles = masterList.filter(row => row.nodeClass === 'PRINCIPLE');
  //
  // // スナップショット用: checkList の保存構造をそのまま表示ソースとして使う
  // const snapshotGroups = useMemo(() => {
  //   if (!useSnapshot || !checkList) return null;
  //   try {
  //     const parsed = JSON.parse(checkList);
  //     return Object.entries(parsed)
  //       .filter(([no]) => no !== IDLE_EQUIPMENT_CHECK_KEY)
  //       .sort(([a], [b]) => Number(a) - Number(b))
  //       .map(([no, group]: [string, any]) => ({ no, group }));
  //   } catch {
  //     return null;
  //   }
  // }, [useSnapshot, checkList]);
  // ↑↑↑

  return (
    <>
      <Button onClick={handleOpenCheckList} m={1}>{t('features.InvestmentScreening.CheckList.button.open')}</Button>

      {isOpen && (
        // 親要素（一覧モーダル等）に transform を持つコンテナが挟まると、
        // position="fixed" の基準がその祖先になってしまい、画面内に収まらず見切れる。
        // Portal で document.body 直下へ描画し、常にビューポート基準で中央表示させる。
        <Portal>
          <Draggable defaultPosition={position} cancel="textarea,select,button,input,a">
            <Box
              position="fixed"
              top={0}
              left={0}
              bg="white"
              p={4}
              border="1px solid #ccc"
              borderRadius="md"
              shadow="lg"
              zIndex="1500"
              width={POPUP_WIDTH}
              maxWidth="calc(100vw - 32px)"
              height={POPUP_HEIGHT}
              maxHeight={POPUP_MAX_HEIGHT}
              display="flex"
              flexDirection="column"
              overflow="auto"
            >
              <Box flexShrink={0} display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box fontWeight="bold">{t('features.InvestmentScreening.CheckList.modal.title')}</Box>
              </Box>

              {/* １０か条チェックリストの確認 */}
              <Box
                flexShrink={0}
                mb={3} mr={4} p={4}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <Text fontWeight="bold" fontSize="md" mb={1}>
                  {t('features.InvestmentScreening.CheckList.tenPrinciplesCheck.title')}
                </Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  {t('features.InvestmentScreening.CheckList.tenPrinciplesCheck.description')}
                </Text>

                <Box
                  display="flex" alignItems="center" gap={3}
                  p={3} mb={3}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Link href={TEN_PRINCIPLES_PORTAL_URL} isExternal color="blue.600" fontWeight="semibold" fontSize="md" wordBreak="break-all" flex="1">
                    {TEN_PRINCIPLES_PORTAL_URL}
                  </Link>
                  <Button as={Link} href={TEN_PRINCIPLES_PORTAL_URL} isExternal size="md" flexShrink={0} _hover={{ textDecoration: "none" }}>
                    {t('features.InvestmentScreening.CheckList.button.openPortal')}
                  </Button>
                </Box>

                {/* 確認チェック */}
                <Checkbox
                  size="lg"
                  colorScheme="green"
                  isChecked={tenPrinciplesChecked}
                  isDisabled={!pagePermissions?.isEditable}
                  onChange={(e) => setTenPrinciplesChecked(e.target.checked)}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {t('features.InvestmentScreening.CheckList.tenPrinciplesCheck.confirmLabel')}
                  </Text>
                </Checkbox>
              </Box>

              {/* 遊休設備の確認 */}
              <Box
                flexShrink={0}
                mb={3} mr={4} p={4}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <Flex align="center" gap={2} mb={1}>
                  <Text fontWeight="bold" fontSize="md">
                    {t('features.InvestmentScreening.CheckList.idleEquipment.title')}
                  </Text>
                  <HelpIcon size="22" >
                    {t('features.InvestmentScreening.CheckList.idleEquipment.idleEquipmentHelp')}
                    <Link href={IDLE_EQUIPMENT_INQUIRY_URL} isExternal color="blue.600" fontWeight="semibold" fontSize="md" wordBreak="break-all" flex="1">
                      {IDLE_EQUIPMENT_INQUIRY_URL}
                    </Link>
                  </HelpIcon>
                </Flex>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  {t('features.InvestmentScreening.CheckList.idleEquipment.description')}
                </Text>

                <Box
                  display="flex" alignItems="center" gap={3}
                  p={3} mb={3}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Link href={IDLE_EQUIPMENT_PORTAL_URL} isExternal color="blue.600" fontWeight="semibold" fontSize="md" wordBreak="break-all" flex="1">
                    {IDLE_EQUIPMENT_PORTAL_URL}
                  </Link>
                  <Button as={Link} href={IDLE_EQUIPMENT_PORTAL_URL} isExternal size="md" flexShrink={0} _hover={{ textDecoration: "none" }}>
                    {t('features.InvestmentScreening.CheckList.button.openPortal')}
                  </Button>
                </Box>

                {/* 確認チェック */}
                <Checkbox
                  size="lg"
                  colorScheme="green"
                  isChecked={idleEquipmentChecked}
                  isDisabled={!pagePermissions?.isEditable}
                  onChange={(e) => setIdleEquipmentChecked(e.target.checked)}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {t('features.InvestmentScreening.CheckList.idleEquipment.confirmLabel')}
                  </Text>
                </Checkbox>
              </Box>

              {/*
            <Box flex={1} minHeight={0} overflow="auto">
              <fieldset disabled={!pagePermissions?.isEditable}>
                <Table size="sm" minW="1480px" width="100%" className="mm-ceas-table" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} >
                  <colgroup>
                    <col width="50px" />
                    <col width="300px" />
                    <col width="240px" />
                    <col width="30px" />
                    <col width="250px" />
                    <col width="120px" />
                    <col />
                  </colgroup>
                  <Thead position="sticky" top="0" zIndex="10" bg="white" >
                    <Tr height={`${ROW_HEIGHT}px`}>
                      <Th>{t('features.InvestmentScreening.CheckList.tableHeader.no')}</Th>
                      <Th>{t('features.InvestmentScreening.CheckList.tableHeader.tenPrinciples')}</Th>
                      <Th>{t('features.InvestmentScreening.CheckList.tableHeader.conflictingConsiderations')}</Th>
                      <Th colSpan={2}>{t('features.InvestmentScreening.CheckList.tableHeader.checkItems')}</Th>
                      <Th>{t('features.InvestmentScreening.CheckList.tableHeader.applicable')}</Th>
                      <Th>{t('features.InvestmentScreening.CheckList.tableHeader.memo')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {snapshotGroups
                      // DBに保存されたスナップショットを使用
                      ? snapshotGroups.map(({ no, group }) => {
                        const sortedItems = Object.entries(group.checkItems ?? {})
                          .sort(([, a]: any, [, b]: any) => (a?.order ?? 0) - (b?.order ?? 0));
                        const rowCount = sortedItems.length;

                        if (rowCount === 0) {
                          return (<Tr key={no} height={`${ROW_HEIGHT}px`}>
                            <Td rowSpan={1} style={{ padding: "0px", textAlign: "center" }}>{no}</Td>
                            <Td rowSpan={1} whiteSpace="break-spaces">{getContentName(group)}</Td>
                            <Td rowSpan={1} colSpan={5} height={`${ROW_HEIGHT}px`}>
                              <Textarea
                                width="100%"
                                height="100%"
                                resize="none"
                                placeholder={t('features.InvestmentScreening.CheckList.placeholder.memo')}
                                draggable="false"
                                value={checkListData[no]?.memo ?? ""}
                                onChange={(e) => { changeMemo(no, e.target.value) }}
                              />
                            </Td>
                          </Tr>);
                        }

                        return sortedItems.map(([nodeId, itemSnapshot]: [string, any], subIdx) => {
                          const subKey = String.fromCharCode(96 + (itemSnapshot?.order ?? subIdx + 1));

                          if (subIdx === 0) {
                            return (<Tr key={nodeId} height={`${ROW_HEIGHT}px`}>
                              <Td rowSpan={rowCount} style={{ padding: "0px", textAlign: "center" }}>{no}</Td>
                              <Td rowSpan={rowCount} whiteSpace="break-spaces">{getContentName(group)}</Td>
                              <Td rowSpan={rowCount} whiteSpace="break-spaces">
                                {getConflictingConsiderations(group).map((text: string, i: number) => (
                                  <Text key={i}>{text}</Text>
                                ))}
                              </Td>
                              <Td style={{ padding: "0px", textAlign: "center" }}>{subKey}</Td>
                              <Td whiteSpace="break-spaces">{getContentName(itemSnapshot ?? {})}</Td>
                              <Td>
                                <Select
                                  value={checkListData[no]?.checkItems?.[nodeId] ?? ""}
                                  onChange={(e) => { changeCheckItems(no, nodeId, e.target.value); }}>
                                  {Object.entries(ApplicableCodeToNameMap).map(([code, name]) => (
                                    <option key={code} value={code}>{name}</option>
                                  ))}
                                </Select>
                              </Td>
                              <Td rowSpan={rowCount} height={`${ROW_HEIGHT * rowCount}px`}>
                                <Textarea
                                  width="100%"
                                  height="100%"
                                  resize="none"
                                  placeholder={t('features.InvestmentScreening.CheckList.placeholder.memo')}
                                  draggable="false"
                                  value={checkListData[no]?.memo ?? ""}
                                  onChange={(e) => { changeMemo(no, e.target.value) }}
                                />
                              </Td>
                            </Tr>);
                          } else {
                            return (
                              <Tr key={nodeId} height={`${ROW_HEIGHT}px`}>
                                <Td style={{ padding: "0px", textAlign: "center" }}>{subKey}</Td>
                                <Td whiteSpace="break-spaces">{getContentName(itemSnapshot ?? {})}</Td>
                                <Td style={{ borderRight: "0px" }}>
                                  <Select
                                    value={checkListData[no]?.checkItems?.[nodeId] ?? ""}
                                    onChange={(e) => { changeCheckItems(no, nodeId, e.target.value); }}>
                                    {Object.entries(ApplicableCodeToNameMap).map(([code, name]) => (
                                      <option key={code} value={code}>{name}</option>
                                    ))}
                                  </Select>
                                </Td>
                              </Tr>
                            );
                          }
                        });
                      })

                      // マスタデータ使用
                      : principles.map((p) => {
                        // この大項目（親）に紐づく子項目（ITEM）をすべて抽出
                        const items = masterList.filter(row => row.nodeClass === 'ITEM' && row.parentNodeId === p.nodeId);

                        // 画面表示用に「その他」を末尾に加える
                        const displayItems: (ChecklistRecord | (Partial<ChecklistRecord> & { nodeId: string; contentNameJa: string; contentNameEn?: string; order: number }))[] = [
                          ...items,
                          {
                            nodeId: `${p.nodeId}_other`,
                            contentNameJa: i18n.t('features.InvestmentScreening.CheckList.other', { lng: 'ja' }),
                            contentNameEn: i18n.t('features.InvestmentScreening.CheckList.other', { lng: 'en' }),
                            order: items.length + 1
                          }
                        ];

                        const rowCount = displayItems.length;

                        if (rowCount === 0) {
                          return (<Tr key={p.nodeId} height={`${ROW_HEIGHT}px`}>
                            <Td rowSpan={1} style={{ padding: "0px", textAlign: "center" }}>{p.checklistNo}</Td>
                            <Td rowSpan={1} whiteSpace="break-spaces">{getContentName(p)}</Td>
                            <Td rowSpan={1} colSpan={5} height={`${ROW_HEIGHT}px`}>
                              <Textarea
                                width="100%"
                                height="100%"
                                resize="none"
                                placeholder={t('features.InvestmentScreening.CheckList.placeholder.memo')}
                                draggable="false"
                                value={checkListData[p.checklistNo]?.memo ?? ""}
                                onChange={(e) => { changeMemo(p.checklistNo, e.target.value) }}
                              />
                            </Td>
                          </Tr>);
                        } else {
                          return (
                            displayItems.map((item, subIdx) => {
                              // 表示用のアルファベット小キー（a, b, c...）をorder（1から始まる連番）から動的に生成
                              const subKey = String.fromCharCode(96 + item.order);

                              if (subIdx === 0) {
                                return (<Tr key={item.nodeId} height={`${ROW_HEIGHT}px`}>
                                  <Td rowSpan={rowCount} style={{ padding: "0px", textAlign: "center" }}>{p.checklistNo}</Td>
                                  <Td rowSpan={rowCount} whiteSpace="break-spaces">{getContentName(p)}</Td>
                                  <Td rowSpan={rowCount} whiteSpace="break-spaces">
                                    {getConflictingConsiderations(p).map((text, i) => {
                                      return (<Text key={i}>{text}</Text>);
                                    })}
                                  </Td>
                                  <Td style={{ padding: "0px", textAlign: "center" }}>{subKey}</Td>
                                  <Td whiteSpace="break-spaces">{getContentName(item)}</Td>
                                  <Td>
                                    <Select
                                      value={checkListData[p.checklistNo]?.checkItems?.[item.nodeId] ?? ""}
                                      onChange={(e) => { changeCheckItems(p.checklistNo, item.nodeId, e.target.value); }}>
                                      {Object.entries(ApplicableCodeToNameMap).map(([code, name]) => (
                                        <option key={code} value={code}>
                                          {name}
                                        </option>
                                      ))}
                                    </Select>
                                  </Td>
                                  <Td rowSpan={rowCount} height={`${ROW_HEIGHT * rowCount}px`}>
                                    <Textarea
                                      width="100%"
                                      height="100%"
                                      resize="none"
                                      placeholder={t('features.InvestmentScreening.CheckList.placeholder.memo')}
                                      draggable="false"
                                      value={checkListData[p.checklistNo]?.memo ?? ""}
                                      onChange={(e) => { changeMemo(p.checklistNo, e.target.value) }}
                                    />
                                  </Td>
                                </Tr>);
                              } else {
                                return (
                                  <Tr key={item.nodeId} height={`${ROW_HEIGHT}px`}>
                                    <Td style={{ padding: "0px", textAlign: "center" }}>{subKey}</Td>
                                    <Td whiteSpace="break-spaces">{getContentName(item)}</Td>
                                    <Td style={{ borderRight: "0px" }}>
                                      <Select
                                        value={checkListData[p.checklistNo]?.checkItems?.[item.nodeId] ?? ""}
                                        onChange={(e) => { changeCheckItems(p.checklistNo, item.nodeId, e.target.value); }}>
                                        {Object.entries(ApplicableCodeToNameMap).map(([code, name]) => (
                                          <option key={code} value={code}>
                                            {name}
                                          </option>
                                        ))}
                                      </Select>
                                    </Td>
                                  </Tr>
                                );
                              }
                            })
                          );
                        }
                      })
                    }
                  </Tbody>
                </Table>
              </fieldset>
            </Box>
            */}
              <Box flexShrink={0} mt={2} display="flex" justifyContent="flex-end">
                <Button colorScheme="blue" ml={1} mr={1} isDisabled={!pagePermissions?.isEditable} onClick={handleSave}>{t('features.InvestmentScreening.CheckList.button.save')}</Button>
                <Button ml={1} mr={1} onClick={() => setIsOpen(false)} aria-label="Close popup">
                  {t('features.InvestmentScreening.CheckList.button.close')}
                </Button>
              </Box>
            </Box>
          </Draggable>
        </Portal>
      )}
    </>
  );
}

export default CheckList;